import { z } from 'zod';

const countrySchema = z.enum(['ES', 'FR']);
export type DocumentCountry = z.infer<typeof countrySchema>;

// Riga MSS/MSF: 1 SKU in 1 pacco (raggruppate via packageNumber)
export const mssLineSchema = z.object({
  lotId: z.string().uuid('Lotto richiesto'),
  productId: z.string().uuid().nullable().optional(),
  lotAnalisiId: z.string().uuid().nullable().optional(),
  packageNumber: z.coerce.number().int().min(1).nullable().optional(),
  qty: z.coerce.number().nonnegative('Quantità >= 0').default(0),
  qty5g: z.coerce.number().int().nonnegative().default(0),
  qty10g: z.coerce.number().int().nonnegative().default(0),
  qty20g: z.coerce.number().int().nonnegative().default(0),
  qty50g: z.coerce.number().int().nonnegative().default(0),
  qty100g: z.coerce.number().int().nonnegative().default(0),
  sku: z.string().max(50).nullable().optional(),
  confezione: z.string().max(200).nullable().optional(),
  note: z.string().max(500).nullable().optional(),
});
export type MssLine = z.infer<typeof mssLineSchema>;

// Voce dizionario shortcut LEGGENDA
export const mssLegendEntrySchema = z.object({
  key: z.string().min(1, 'Chiave obbligatoria').max(50),
  value: z.string().min(1, 'Valore obbligatorio').max(500),
});
export type MssLegendEntry = z.infer<typeof mssLegendEntrySchema>;

// Create
export const mssCreateRequestSchema = z.object({
  draftName: z.string().min(1).max(100).nullable().optional(),
  country: countrySchema,
  customerId: z.string().uuid().nullable().optional(),
  workDate: z.coerce.date(),
  notes: z.string().max(2000).nullable().optional(),
  lines: z.array(mssLineSchema).default([]),
  legendEntries: z.array(mssLegendEntrySchema).default([]),
});
export type MssCreateRequest = z.infer<typeof mssCreateRequestSchema>;

// Update: tutti opzionali, valido solo se status=DRAFT && !locked
export const mssUpdateRequestSchema = mssCreateRequestSchema.partial();
export type MssUpdateRequest = z.infer<typeof mssUpdateRequestSchema>;

// Close: trigger transition DRAFT -> CLOSED + locked=true
export const mssCloseRequestSchema = z.object({}).strict();
export type MssCloseRequest = z.infer<typeof mssCloseRequestSchema>;

const documentStatusSchema = z.enum(['DRAFT', 'CLOSED', 'SUPERSEDED']);
const documentTypeSchema = z.enum(['PSC', 'LNP', 'MPG', 'MSS', 'MSF', 'INV', 'SAP']);

export const mssLineResponseSchema = z.object({
  id: z.string(),
  lineNumber: z.number().int().positive(),
  direction: z.enum(['IN', 'OUT']),
  lotId: z.string().nullable(),
  productId: z.string().nullable(),
  lotAnalisiId: z.string().nullable(),
  packageNumber: z.number().int().nullable(),
  qty: z.string(),
  qty5g: z.number().int(),
  qty10g: z.number().int(),
  qty20g: z.number().int(),
  qty50g: z.number().int(),
  qty100g: z.number().int(),
  sku: z.string().nullable(),
  confezione: z.string().nullable(),
  note: z.string().nullable(),
  lotCode: z.string().nullable(),
  productCode: z.string().nullable(),
  productName: z.string().nullable(),
  lotAnalisiCodice: z.string().nullable(),
  lotAnalisiDataAnalisi: z.string().nullable(),
});
export type MssLineResponse = z.infer<typeof mssLineResponseSchema>;

export const mssLegendEntryResponseSchema = z.object({
  id: z.string(),
  country: countrySchema,
  key: z.string(),
  value: z.string().nullable(),
  sourceDocumentId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MssLegendEntryResponse = z.infer<typeof mssLegendEntryResponseSchema>;

export const mssResponseSchema = z.object({
  id: z.string(),
  type: z.enum(['MSS', 'MSF']),
  code: z.string().nullable(),
  draftName: z.string().nullable(),
  country: countrySchema,
  customerId: z.string().nullable(),
  customer: z
    .object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      country: z.string(),
    })
    .nullable(),
  workDate: z.string(),
  periodId: z.string(),
  period: z.object({
    year: z.number().int(),
    month: z.number().int(),
    status: z.enum(['OPEN', 'CLOSED']),
  }),
  status: documentStatusSchema,
  locked: z.boolean(),
  notes: z.string().nullable(),
  createdById: z.string(),
  closedById: z.string().nullable(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
  updatedAt: z.string(),
  lines: z.array(mssLineResponseSchema),
  // Editor LEGGENDA durante DRAFT (salvato in dataJson.legendEntries)
  draftLegendEntries: z.array(mssLegendEntrySchema),
  // Computed
  pacchiCount: z.number().int().nonnegative(),
  totalQtyG: z.string(),
  lotsCount: z.number().int().nonnegative(),
});
export type MssResponse = z.infer<typeof mssResponseSchema>;

export const mssListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  status: documentStatusSchema.optional(),
  country: countrySchema.optional(),
  customerId: z.string().uuid().optional(),
  periodId: z.string().uuid().optional(),
  workDateFrom: z.coerce.date().optional(),
  workDateTo: z.coerce.date().optional(),
});
export type MssListQuery = z.infer<typeof mssListQuerySchema>;

export const mssListItemSchema = z.object({
  id: z.string(),
  type: z.enum(['MSS', 'MSF']),
  code: z.string().nullable(),
  draftName: z.string().nullable(),
  country: countrySchema,
  customerName: z.string().nullable(),
  workDate: z.string(),
  status: documentStatusSchema,
  locked: z.boolean(),
  closedAt: z.string().nullable(),
  pacchiCount: z.number().int().nonnegative(),
  totalQtyG: z.string(),
});
export type MssListItem = z.infer<typeof mssListItemSchema>;

export const mssListResponseSchema = z.object({
  items: z.array(mssListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type MssListResponse = z.infer<typeof mssListResponseSchema>;

// LotAnalisi response (per picker)
export const lotAnalisiResponseSchema = z.object({
  id: z.string(),
  lotId: z.string(),
  codice: z.string(),
  dataAnalisi: z.string(),
  fileUrl: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LotAnalisiResponse = z.infer<typeof lotAnalisiResponseSchema>;

export const lotAnalisiListQuerySchema = z.object({
  lotId: z.string().uuid().optional(),
});
export type LotAnalisiListQuery = z.infer<typeof lotAnalisiListQuerySchema>;

// Re-export DocumentType utility per consistenza
export { documentTypeSchema };
